/**
 * Modal dialog primitive (settings popup, scrape-now popup, post preview — PRD), in the kit
 * Modal's language: hairline head with title + quiet close, soft rise animation, the body as the
 * single scroll container. Centered panel with size variants (the kit's edge-to-edge shell fits
 * its data editor; these dialogs are conversations).
 *
 * Closes on Escape, on the close button and (optionally) on a backdrop click; locks page scroll,
 * moves focus into the dialog while open and returns it to the opener on close.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLatest } from '../lib/hooks';
import { CloseIcon } from './icons';
import { cx } from './cx';

/**
 * Open dialogs, oldest first. Escape listeners sit on `document`, so they all fire for the same
 * event and `stopPropagation` cannot shield a sibling listener on the same target — without this,
 * one Escape over a dialog stacked on another would close both. Only the top entry acts.
 * (Scroll-lock and focus restore already nest correctly: each effect saves and restores what it
 * found, and cleanups run innermost-first.)
 */
const openModals: string[] = [];

export type ModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  /** Rendered in the head between the title and the close button. */
  headActions?: ReactNode;
  /** `full` fills the viewport minus the overlay padding — a table that wants every column. */
  size?: 'sm' | 'md' | 'lg' | 'full';
  /** Default `true`; set `false` for dialogs that must be answered. */
  closeOnBackdrop?: boolean;
  /** Remove the body's default padding (tables, previews). */
  flushBody?: boolean;
  className?: string;
};

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  headActions,
  size = 'md',
  closeOnBackdrop = true,
  flushBody = false,
  className,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Keyed on `open` alone, with `onClose` read through a ref: callers pass inline closures whose
  // identity changes every render, and re-running this effect would re-focus the panel and steal
  // focus from whatever field the user is typing into.
  const latestOnClose = useLatest(onClose);
  useEffect(() => {
    if (!open) return;
    openModals.push(titleId);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Only the topmost dialog answers; the ones below it stay open.
      if (openModals[openModals.length - 1] !== titleId) return;
      event.stopPropagation();
      latestOnClose.current();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const previousFocus = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      const index = openModals.lastIndexOf(titleId);
      if (index >= 0) openModals.splice(index, 1);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [open, latestOnClose, titleId]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={cx('modal', `modal--${size}`, className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <header className="modal__head">
          <h2 className="modal__title" id={titleId}>
            {title}
          </h2>
          <div className="modal__head-actions">
            {headActions}
            <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
              <CloseIcon size={14} />
            </button>
          </div>
        </header>
        <div className={cx('modal__body', flushBody && 'modal__body--flush')}>{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
